/**
 * QA 메모 — 주차 테스트 아래에 댓글처럼 편하게 남기는 현장별 메모. 서버에 저장돼 QA 를 보는
 * 모두가 같은 목록을 본다. 여러 사람이 동시에 쓰므로 등록할 때마다 목록을 다시 읽는다.
 * 지우기는 본인 메모 또는 관리자만. 돌아다니며 휴대폰으로 쓰므로 버튼은 손가락 크기(44px)로 둔다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Comment as CommentIcon,
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from '@mui/icons-material';

import AuthorChip from '../AuthorChip';
import { SiteQaNote } from '../../models/SiteQaNote';
import { SiteQaNoteService } from '../../services/SiteQaNoteService';
import usePermissions from '../../hooks/usePermissions';
import { getUserInfo } from '../../utils/auth';

const when = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

const QaNotesPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const { isAdmin } = usePermissions();
  const myId = getUserInfo()?.user_id;
  const [notes, setNotes] = useState<SiteQaNote[] | null>(null);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setNotes(await SiteQaNoteService.list(projectId));
      setError(null);
    } catch (e) {
      setError(errorText(e, '메모 목록 조회 실패'));
      setNotes((cur) => cur ?? []);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async () => {
    const text = body.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await SiteQaNoteService.create(projectId, text);
      setBody('');
      await load();
    } catch (e) {
      setError(errorText(e, '메모 작성 실패'));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (note: SiteQaNote) => {
    if (!window.confirm('이 메모를 지울까요?')) return;
    try {
      await SiteQaNoteService.delete(note.id);
      await load();
    } catch (e) {
      setError(errorText(e, '메모 삭제 실패'));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, mt: 2 }} data-testid="qa-notes">
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
        <CommentIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
          메모 {notes?.length ?? ''}
        </Typography>
        <IconButton onClick={load} aria-label="메모 새로고침" sx={{ width: 44, height: 44 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {notes === null ? (
        <CircularProgress size={18} />
      ) : notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          아직 메모가 없습니다. 확인한 내용이나 이상한 점을 남겨 두세요.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ mb: 1.5 }} data-testid="qa-notes-list">
          {notes.map((n) => (
            <Box key={n.id} sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                <AuthorChip name={n.created_by_name} userId={n.created_by_id} label="" dense />
                <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                  {when(n.created_at)}
                </Typography>
                {(isAdmin || (myId != null && n.created_by_id === myId)) && (
                  <IconButton onClick={() => remove(n)} aria-label="메모 삭제" sx={{ width: 44, height: 44, my: -1 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {n.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          placeholder="메모를 남기세요"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          maxRows={6}
          // 16px 이상이어야 iOS 가 입력할 때 화면을 확대하지 않는다.
          inputProps={{ 'data-testid': 'qa-notes-input', enterKeyHint: 'send', style: { fontSize: 16 } }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              post();
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={post}
          disabled={posting || !body.trim()}
          aria-label="메모 등록"
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          {posting ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Stack>
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default QaNotesPanel;
